import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { RootState } from 'store';
import { saveCollateral } from 'store/ohaFormReducer';
import TitleBar from '../DismissalJointLiabilityMutation/components/TitleBar';
import BottomNavigation from '../DismissalJointLiabilityMutation/components/BottomNavigation';
import Collateral from '../../templates/Collateral';
import { CollateralFormType, FormStepProps } from 'screens/Mutations/utils/types';
import { collateralSchema } from '../../schemas/collateral';
import { useOhaFormStep } from 'screens/Mutations/hooks/useOhaFormStep';
import { mapOHAForm } from 'screens/Mutations/utils/mappers/oha/mapFormToApi';
import FormTemplate from 'components/Templates/FormTemplate';
import FormStepper from '../DismissalJointLiabilityMutation/components/FormStepper';
import { Form } from 'components/Molecules/components/Form/Form.styled';
import CustomSnackbar from 'components/Snackbar/CustomSnackbar';

const CollateralForm: FC<FormStepProps> = ({ title, subtitle }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState<{ status: string; name: string } | undefined>();
  const ohaFormState = useSelector((state: RootState) => state.ohaForm);

  const formMethods = useForm<CollateralFormType>({
    defaultValues: { ...ohaFormState.collateral },
    resolver: yupResolver(collateralSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = formMethods;

  const collateralFormValues = watch();

  const completeForm = {
    ...ohaFormState,
    collateral: collateralFormValues,
  };

  const onSaveError = () => handleNotification('error', 'submit');

  const { save, isSaving, goToNextStep } = useOhaFormStep({ onSaveError });

  const onSubmit = async () => {
    dispatch(saveCollateral(collateralFormValues));

    if (isDirty) {
      await save(mapOHAForm(completeForm));
    }
    goToNextStep();
  };

  const dispatchForm = useCallback(() => {
    dispatch(saveCollateral(collateralFormValues));
  }, [collateralFormValues, dispatch]);

  const handleNotification = useCallback(
    (status: string, name: string) => setSnackbar({ status, name }),
    []
  );

  const handleOnClose = useCallback(() => setSnackbar(undefined), []);

  return (
    <>
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormTemplate
            header={
              <TitleBar
                onResult={handleNotification}
                formState={completeForm}
                resetForm={reset}
                isFormDirty={isDirty}
                dispatchCurrentForm={dispatchForm}
              />
            }
            sidebar={<FormStepper />}
            footer={
              <BottomNavigation dispatchCurrentForm={dispatchForm} isLoadingSubmit={isSaving} />
            }
          >
            <Collateral title={t(title)} subtitle={t(subtitle)} />
          </FormTemplate>
        </Form>
      </FormProvider>
      {snackbar && (
        <CustomSnackbar
          isOpen={!!snackbar}
          handleClose={handleOnClose}
          severity={snackbar.status === 'success' ? 'success' : 'error'}
          message={t(`directMutations.snackbar.${snackbar.name}.${snackbar.status}`)}
        />
      )}
    </>
  );
};

export default CollateralForm;
